import { Head, useForm } from "@inertiajs/react";
import { t } from "@travily/i18n";
import { productEditorPath, productEditorSectionPath, productPath } from "@travily/routes";
import type ListingsEditorPriceType from "@travily/types/pages/ListingsEditorPriceType";
import { ButtonDock } from "@ui/components/ButtonDock";
import { StandardButton } from "@ui/components/StandardButton";
import type { ReactNode } from "react";
import { EditFrame } from "@/layouts/edit-frame";
import PersistentLayout from "@/layouts/persistent-layout";
import { buildEditorSections } from "@/lib/editor-sections";
import { ProductPriceTypeEditor } from "./show.view";

type PageProps = ListingsEditorPriceType;

interface PriceTypeFormData {
  price_type: "free" | "paid";
}

const ProductEditPriceTypePage = (props: PageProps) => {
  const { listing_id } = props;
  const sections = buildEditorSections(props);
  const form = useForm<PriceTypeFormData>({ price_type: props.price_type as "free" | "paid" });

  const handleSave = () => {
    form.patch(productEditorSectionPath(listing_id, "price_type"), {
      preserveScroll: "errors",
    });
  };

  return (
    <EditFrame
      activeSection="price_type"
      actions={
        <ButtonDock
          primaryAction={
            <>
              <StandardButton
                className="sm:hidden"
                color="green"
                isDisabled={!form.isDirty || form.processing}
                onPress={handleSave}
                size="56"
              >
                {t("common.actions.save")}
              </StandardButton>
              <StandardButton
                className="hidden sm:flex"
                color="green"
                isDisabled={!form.isDirty || form.processing}
                onPress={handleSave}
                size="64"
              >
                {t("common.actions.save")}
              </StandardButton>
            </>
          }
        />
      }
      backHref={productPath(listing_id)}
      href={productEditorPath(listing_id)}
      sections={sections}
      title={t("web.products.editors.show.title")}
    >
      <Head title={t("web.products.editor.sections.show.meta_title", { title: props.listing_title ?? "" })} />
      <ProductPriceTypeEditor
        onChange={(value) => form.setData("price_type", value as "free" | "paid")}
        stripeConnected={props.stripe_connected}
        value={form.data.price_type}
      />
    </EditFrame>
  );
};

ProductEditPriceTypePage.layout = (page: ReactNode) => <PersistentLayout>{page}</PersistentLayout>;

export default ProductEditPriceTypePage;
